import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AppShell from "@/components/platform/AppShell";
import { PageTitle } from "@/components/platform/Badges";
import { AuditTimeline } from "@/components/platform/AuditTimeline";
import { api } from "@/lib/api";

const ALL = "__all";
const PAGE = 50;

const Facet = ({ value, onChange, placeholder, options, testId, labels = {} }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-[170px] text-xs bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{labels[o] || o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function AuditLog() {
  const [facets, setFacets] = useState({ modules: [], actors: [], event_types: [] });
  const [labels, setLabels] = useState({});
  const [f, setF] = useState({ q: "", module: ALL, actor: ALL, event_type: ALL, date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });

  useEffect(() => {
    api.get("/platform/audit/facets").then(({ data: d }) => setFacets(d)).catch(() => {});
    api.get("/platform/meta").then(({ data: d }) => setLabels(d.event_types)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const p = { page, page_size: PAGE, q: f.q || undefined, date_from: f.date_from || undefined, date_to: f.date_to || undefined };
    ["module", "actor", "event_type"].forEach((k) => { if (f[k] !== ALL) p[k] = f[k]; });
    const { data: d } = await api.get("/platform/audit", { params: p });
    setData(d);
  }, [f, page]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const set = (k) => (v) => { setPage(1); setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v })); };
  const pages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <AppShell testId="audit-log-page">
      <PageTitle eyebrow="Audit log engine" title="Audit Log"
                 subtitle="Every create, update, status change, owner change, attachment, evidence upload and approval — with actor, time and before/after values." />
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-xl p-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={f.q} onChange={set("q")} placeholder="Search object / actor" className="h-9 pl-8 w-[220px] text-xs" data-testid="audit-search" />
        </div>
        <Facet value={f.module} onChange={set("module")} placeholder="All modules" options={facets.modules} testId="audit-filter-module" />
        <Facet value={f.event_type} onChange={set("event_type")} placeholder="All events" options={facets.event_types} labels={labels} testId="audit-filter-event" />
        <Facet value={f.actor} onChange={set("actor")} placeholder="All actors" options={facets.actors} testId="audit-filter-actor" />
        <Input type="date" value={f.date_from} onChange={set("date_from")} className="h-9 w-[150px] text-xs" data-testid="audit-date-from" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" value={f.date_to} onChange={set("date_to")} className="h-9 w-[150px] text-xs" data-testid="audit-date-to" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-slate-500" data-testid="audit-total"><span className="font-mono font-semibold text-[#003366]">{data.total}</span> events</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs font-mono text-slate-600" data-testid="audit-page">{page} / {pages}</span>
            <Button size="sm" variant="outline" className="h-8" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <AuditTimeline items={data.items} testId="audit-log-timeline" />
      </div>
    </AppShell>
  );
}
